import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import {
	ComplexFlowUnconfigedState,
	NodesState,
} from 'views/complex-onboarding-flow/stores/states';
import { useFlowTotals } from '../flow-total/use-flow-totals';

// Same four catalog keys the Actions List tags "Included" (builder-left-nav.tsx)
// — presence-only steps with no price and nothing to configure.
const INCLUDED_STEP_IDS = new Set([
	'basicInfo',
	'secondContact',
	'kybForm',
	'accountForm',
]);

/**
 * The canvas stat strip (Concept E, "Panel Ledger"). Every figure here reads
 * an already-real source of state — nothing computed twice, nothing invented:
 * - Priced nodes / Flow total: useFlowTotals(), the same hook the footer's
 *   FlowTotal renders from.
 * - Unconfigured: ComplexFlowUnconfigedState, the same list handleDisableNext
 *   and setNextStep already gate on.
 * - Included steps: nodes on canvas whose stepId is one of the catalog's own
 *   presence-only entries.
 */
export const StatStrip = () => {
	const { low, high, steps, isRange } = useFlowTotals();
	const complexFlowConfiged = useRecoilValue(ComplexFlowUnconfigedState);
	const nodes = useRecoilValue(NodesState);

	// Authentication is excluded on purpose: it has no configurable fields (its
	// gear is hidden on the node for the same reason), so counting it as a step
	// that "needs a setting" pointed at a node with nothing to open.
	const unconfigured = useMemo(
		() =>
			(complexFlowConfiged ?? []).filter(
				el => !el.configed && (el as any)?.stepId !== 'authentication'
			).length,
		[complexFlowConfiged]
	);
	const included = useMemo(
		() =>
			(nodes ?? []).filter((n: any) => INCLUDED_STEP_IDS.has(n?.stepId)).length,
		[nodes]
	);

	if (!nodes?.length) return null;

	return (
		<div className="PanelLedger__stats">
			<div className="PanelLedger__stat">
				<div className="l">Priced nodes</div>
				<div className="n bl">{steps}</div>
				<div className="d">on canvas now</div>
			</div>
			<div className="PanelLedger__stat">
				<div className="l">Unconfigured</div>
				<div className="n am">{unconfigured}</div>
				<div className="d">need a setting</div>
			</div>
			<div className="PanelLedger__stat">
				<div className="l">Included steps</div>
				<div className="n gr">{included}</div>
				<div className="d">no charge to applicant</div>
			</div>
			<div className="PanelLedger__stat">
				<div className="l">Flow total</div>
				<div className="n total">
					{`$${low.toFixed(2)}${isRange ? ` – $${high.toFixed(2)}` : ''}`}
				</div>
				<div className="d">per applicant</div>
			</div>
		</div>
	);
};
